import { useState, useEffect, useMemo, useRef } from 'react';
import { useNotifications } from '../../context/NotificationsContext';
import { useUserLocation } from '../../context/LocationContext';
import { useCart } from '../../hooks/useCart';
import { useThemeColor } from '../../hooks/useThemeColor';
import { useStickyHeaderObservers } from './hooks/useStickyHeaderObservers';
import { useMenuFilters, applyMenuFilters } from './hooks/useMenuFilters';
import { useAvisarHomeListo } from './hooks/useAvisarHomeListo';
import { useRestaurantMenu } from '../../hooks/useRestaurantMenu';
import { useDefaultRestaurant } from '../../hooks/useRestaurantEta';
import { useColorHeader } from '../../hooks/useColorHeader';
import MarketSectionView from './MarketSectionView';

// Single-restaurant: contenedor del home. Trae el menú del restaurante único
// (categorías + productos), le aplica los filtros y maneja scroll/estado.
export default function MarketSectionPage({
  isActive = true,
  searchOverlayOpen,
  setSearchOverlayOpen,
  scrollContainerRef,
}) {
  const sticky = useStickyHeaderObservers({ scrollContainerRef });

  const menu = useRestaurantMenu();
  const restaurant = useDefaultRestaurant();
  const filtersUI = useMenuFilters();
  const colorHeader = useColorHeader(restaurant);

  // Menú ya filtrado/ordenado: alimenta tanto los chips como las secciones, así
  // no queda un chip que salte a una categoría vacía.
  const menuCategories = useMemo(
    () => applyMenuFilters(menu.categories, filtersUI.filters),
    [menu.categories, filtersUI.filters]
  );

  // Populares es una sección curada, no un resultado de búsqueda: mientras haya
  // filtros activos se oculta para no contradecirlos.
  const popularProducts = filtersUI.filtersActive ? [] : menu.popularProducts;

  // El splash se queda hasta que esto avisa que la primera pantalla está lista.
  useAvisarHomeListo({ activo: isActive, restaurant, menuCargando: menu.loading, categorias: menu.categories });

  const { pushSupported, pushPermission, requestPushPermission } = useNotifications();
  const { openLocationModal } = useUserLocation();
  const { addItem, items: cartItems } = useCart();

  const [activeCategory, setActiveCategory] = useState(null);
  const [showPushModal, setShowPushModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedProductBusiness, setSelectedProductBusiness] = useState(null);
  const sectionRefs = useRef({});

  // Barra de estado del color del header (llega hasta arriba, como app
  // nativa); en blanco cuando el buscador ya se volvió blanco al pegarse. Con
  // imagen de fondo, el verde de arriba de esa imagen; sin ella, el color liso
  // del header.
  useThemeColor(
    !isActive
      ? null
      : searchOverlayOpen || sticky.categoriesStuck
      ? '#ffffff'
      : restaurant?.headerBackground
      ? '--color-statusbar'
      : colorHeader || '--color-header-bg'
  );

  // Selecciona la primera categoría al cargar, y re-selecciona si la activa
  // desapareció al aplicar un filtro. La primera DE LA BARRA: las que no salen
  // ahí ("Disponibles todo el día") no tienen chip que marcar.
  useEffect(() => {
    if (menuCategories.length === 0) return;
    const stillThere = menuCategories.some((c) => c._id === activeCategory);
    const primera = menuCategories.find((c) => c.showInCategoryBar) || menuCategories[0];
    if (!stillThere) setActiveCategory(primera._id);
  }, [menuCategories, activeCategory]);

  useEffect(() => {
    const timer = setTimeout(() => {
      const dismissed = localStorage.getItem('pushModalDismissed');
      if (pushSupported && pushPermission === 'default' && !dismissed) {
        setShowPushModal(true);
      }
    }, 2000);
    return () => clearTimeout(timer);
  }, [pushSupported, pushPermission]);

  // Producto del menú: el "negocio" es siempre el restaurante único (para
  // inyectar businessId al agregar al carrito).
  const handleMenuProductClick = (product) => {
    setSelectedProduct(product);
    setSelectedProductBusiness(restaurant);
  };

  const handleAddToCartFromDetail = (product, quantity = 1, extras = []) => {
    if (selectedProductBusiness) {
      const businessId = selectedProductBusiness._id || selectedProductBusiness.id;
      addItem({ ...product, businessId }, quantity, extras);
    }
    setSelectedProduct(null);
  };

  const handleCategoryClick = (catId) => {
    setActiveCategory(catId);
    const target = sectionRefs.current[catId];
    if (!target) return;
    const headerOffset = 180;
    const container = scrollContainerRef?.current;
    if (container) {
      const targetRect = target.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();
      const offsetPosition = container.scrollTop + (targetRect.top - containerRect.top) - headerOffset;
      container.scrollTo({ top: offsetPosition, behavior: 'smooth' });
    } else {
      const offsetPosition = target.getBoundingClientRect().top + window.pageYOffset - headerOffset;
      window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
    }
  };

  const handlePushRequest = async () => {
    await requestPushPermission();
    setShowPushModal(false);
  };

  return (
    <MarketSectionView
      menuCategories={menuCategories}
      menuLoading={menu.loading}
      menuError={menu.error}
      filtersUI={filtersUI}
      popularProducts={popularProducts}
      popularSection={{
        background: restaurant?.popularBackground,
        title: restaurant?.popularTitle,
        titleType: restaurant?.popularTitleType,
        addButtonImage: restaurant?.addButtonImage,
      }}
      promoSection={restaurant?.promoSection}
      menuButtonImage={restaurant?.menuButtonImage}
      unavailableTagImage={restaurant?.unavailableTagImage}
      menuAddButtonImage={restaurant?.menuAddButtonImage}
      footerImage={restaurant?.footerImage}
      filterIcons={restaurant?.filterIcons}
      onMenuProductClick={handleMenuProductClick}
      sticky={sticky}
      activeCategory={activeCategory}
      sectionRefs={sectionRefs}
      selectedProduct={selectedProduct}
      searchOverlayOpen={searchOverlayOpen}
      showPushModal={showPushModal}
      cartItemsCount={cartItems.length}
      onOpenLocationModal={openLocationModal}
      onSearchTap={() => setSearchOverlayOpen(true)}
      onCloseSearchOverlay={() => setSearchOverlayOpen(false)}
      onCategoryClick={handleCategoryClick}
      onCloseProductDetail={() => setSelectedProduct(null)}
      onAddToCartFromDetail={handleAddToCartFromDetail}
      onPushRequest={handlePushRequest}
      onPushDismiss={() => setShowPushModal(false)}
      scrollContainerRef={scrollContainerRef}
      isActive={isActive}
    />
  );
}
